import React, { useState } from "react";
import { Switch, Tooltip } from "@mui/material";
import RouteSelect from "../reusables/RouteSelect";
import AudioPlayerForRouting from "../reusables/AudioPlayerForRouting";
import AddRecordingFlowWrapper from "../modals/AddRecordingFlowWrapper";
import { findRecording } from "../../utils/dialerPreferences";
import { brand } from "../../theme/brandTokens";

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: brand.primary },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: brand.primary,
  },
  "& .MuiSwitch-switchBase.Mui-disabled": { color: "#D1D5DB" },
  "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
    backgroundColor: "#E5E7EB",
  },
};

/**
 * Greeting / voicemail drop pickers for the Dialer's Advanced Settings section.
 * Picks are per user; `onChange` receives a partial preferences patch.
 */
const DialerAudioOptions = ({
  recordings,
  setRecordings,
  preferences,
  onChange,
  saveError,
}) => {
  const [addRecordingOpen, setAddRecordingOpen] = useState(false);
  const [addRecordingTarget, setAddRecordingTarget] = useState(null);

  const options = (recordings || [])
    .filter((r) => r?.M?.rec_id?.S && r?.M?.url?.S)
    .map((r) => ({ value: r.M.rec_id.S, label: r.M.title?.S || "Recording" }));

  const greeting = findRecording(recordings, preferences.greeting_rec_id);
  const vmDrop = findRecording(recordings, preferences.vm_drop_rec_id);

  const addOptionFor = (key) => ({
    label: "Add Recording",
    onClick: () => {
      setAddRecordingTarget(key);
      setAddRecordingOpen(true);
    },
  });

  return (
    <div className="mt-4 pt-3 border-t border-gray-200 space-y-3">
      <div>
        <div className="text-[14px] text-gray-700 mb-1">Greeting</div>
        <div className="text-[12px] text-gray-500 mb-2">
          Plays to the person you call. You stay on the line.
        </div>
        <RouteSelect
          value={greeting ? preferences.greeting_rec_id : ""}
          onChange={(value) => onChange({ greeting_rec_id: value || "" })}
          options={options}
          placeholder="None"
          addOption={addOptionFor("greeting_rec_id")}
        />
        {greeting && (
          <div className="mt-2">
            <AudioPlayerForRouting src={greeting.url} compact />
          </div>
        )}
        <div className="mt-2 flex items-center justify-between">
          <span
            className={`text-[14px] ${greeting ? "text-gray-700" : "text-gray-400"}`}
          >
            Auto-play when a person answers
          </span>
          <Tooltip
            title={
              greeting
                ? "Plays the greeting automatically when it sounds like a person picked up. Voicemail, phone menus, and call screening are skipped."
                : "Choose a greeting first."
            }
            arrow
          >
            <span>
              <Switch
                checked={!!greeting && preferences.auto_greeting}
                disabled={!greeting}
                onChange={(e) => onChange({ auto_greeting: e.target.checked })}
                sx={switchSx}
              />
            </span>
          </Tooltip>
        </div>
      </div>

      <div>
        <div className="text-[14px] text-gray-700 mb-1">Voicemail drop</div>
        <div className="text-[12px] text-gray-500 mb-2">
          Click it when you reach voicemail. The message plays after the greeting, and you’re free for the next call.
        </div>
        <RouteSelect
          value={vmDrop ? preferences.vm_drop_rec_id : ""}
          onChange={(value) => onChange({ vm_drop_rec_id: value || "" })}
          options={options}
          placeholder="None"
          addOption={addOptionFor("vm_drop_rec_id")}
        />
        {vmDrop && (
          <div className="mt-2">
            <AudioPlayerForRouting src={vmDrop.url} compact />
          </div>
        )}
      </div>

      {saveError && <div className="text-[12px] text-red-600">{saveError}</div>}

      <AddRecordingFlowWrapper
        open={addRecordingOpen}
        setOpen={setAddRecordingOpen}
        recordings={recordings}
        setRecordings={setRecordings}
        callRecordingAddedAndClear={(newId) => {
          if (addRecordingTarget) onChange({ [addRecordingTarget]: newId });
          setAddRecordingTarget(null);
        }}
      />
    </div>
  );
};

export default DialerAudioOptions;
